/**
 * Touch gestures on the stage (phones, tablets, the installed app): swipes and a double tap.
 * Only touch input counts, so the mouse and the keyboard behave as before.
 */

export type Gesture = 'swipe-left' | 'swipe-right' | 'swipe-up' | 'swipe-down' | 'double-tap';

export interface Stroke {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Milliseconds from touch down to touch up. */
  ms: number;
}

/** A swipe travels at least this far (CSS px), quickly, mostly along one axis. */
const SWIPE_MIN_PX = 60;
const SWIPE_MAX_MS = 700;
const SWIPE_AXIS_RATIO = 1.5;
/** A tap barely moves and is short. */
const TAP_MAX_PX = 12;
const TAP_MAX_MS = 300;
/** Two taps this close in time and place make a double tap. */
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_PX = 40;

/** What a single finger stroke was: a swipe, a tap, or nothing. */
export function classifyStroke(s: Stroke): Gesture | 'tap' | null {
  const dx = s.x1 - s.x0;
  const dy = s.y1 - s.y0;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (Math.hypot(dx, dy) <= TAP_MAX_PX) return s.ms <= TAP_MAX_MS ? 'tap' : null;
  if (s.ms > SWIPE_MAX_MS) return null;
  if (ax >= SWIPE_MIN_PX && ax >= ay * SWIPE_AXIS_RATIO)
    return dx < 0 ? 'swipe-left' : 'swipe-right';
  if (ay >= SWIPE_MIN_PX && ay >= ax * SWIPE_AXIS_RATIO) return dy < 0 ? 'swipe-up' : 'swipe-down';
  return null;
}

export interface Tap {
  x: number;
  y: number;
  t: number;
}

/** Whether a tap completes a double tap with the one before it. */
export function isDoubleTap(prev: Tap | null, tap: Tap): boolean {
  return (
    !!prev &&
    tap.t - prev.t <= DOUBLE_TAP_MS &&
    Math.hypot(tap.x - prev.x, tap.y - prev.y) <= DOUBLE_TAP_PX
  );
}

/**
 * Watch `el` for touch gestures. One finger only: a second finger cancels the stroke. Also stops
 * the browser's own touch gestures there (pull to refresh, swipe back/forward, zooming).
 * Returns a function that stops watching.
 */
export function watchGestures(el: HTMLElement, on: (g: Gesture) => void): () => void {
  let start: { id: number; x: number; y: number; t: number } | null = null;
  let lastTap: Tap | null = null;
  let fingers = 0;

  const down = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    fingers++;
    start = fingers === 1 ? { id: e.pointerId, x: e.clientX, y: e.clientY, t: e.timeStamp } : null;
  };
  const up = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    fingers = Math.max(0, fingers - 1);
    const s = start;
    if (!s || s.id !== e.pointerId) return;
    start = null;
    const g = classifyStroke({
      x0: s.x,
      y0: s.y,
      x1: e.clientX,
      y1: e.clientY,
      ms: e.timeStamp - s.t,
    });
    if (g === 'tap') {
      const tap = { x: e.clientX, y: e.clientY, t: e.timeStamp };
      if (isDoubleTap(lastTap, tap)) {
        lastTap = null;
        on('double-tap');
      } else lastTap = tap;
    } else if (g) {
      lastTap = null;
      on(g);
    }
  };
  const cancel = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    fingers = Math.max(0, fingers - 1);
    start = null;
  };
  // Safari ignores touch-action for some of its gestures (rubber-banding, edge swipes).
  const block = (e: TouchEvent) => {
    if (e.cancelable) e.preventDefault();
  };

  el.addEventListener('pointerdown', down);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', cancel);
  el.addEventListener('touchmove', block, { passive: false });
  return () => {
    el.removeEventListener('pointerdown', down);
    el.removeEventListener('pointerup', up);
    el.removeEventListener('pointercancel', cancel);
    el.removeEventListener('touchmove', block);
  };
}
